import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import DefaultLogo from "../../Images/Hrlabs_Logo.jpeg";
import SidebarItem from "./SidebarItem";
import {
  SIDEBAR_SECTIONS,
  SIDEBAR_FOOTER_ITEMS,
  isItemActive,
} from "./sidebarMenu";
import "./Sidebar.scss";

const COLLAPSE_STORAGE_KEY = "sidebar-collapsed";
// Must match the `max-width: 992px` breakpoint in Sidebar.scss.
const MOBILE_QUERY = "(max-width: 992px)";

const readInitialCollapsed = () => {
  try {
    const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    // localStorage unavailable (private mode etc.) - fall through to default
  }
  // First visit: start collapsed on narrower laptop/tablet screens.
  return typeof window !== "undefined" && window.innerWidth < 1200;
};

/**
 * Props
 *  - notificationCounts : { [countKey]: number }   (from AppLayout)
 *  - mobileOpen         : boolean - drawer state (owned by AppLayout so the
 *                         Navbar hamburger can toggle it)
 *  - onMobileClose      : () => void
 */
const Sidebar = ({
  notificationCounts = {},
  mobileOpen = false,
  onMobileClose,
}) => {
  const { user, hasRole } = useAuth();
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);

  const [collapsed, setCollapsed] = useState(readInitialCollapsed);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches,
  );

  // Persist the collapse preference.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
    } catch {
      // non-critical
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((prev) => !prev), []);

  // Track the mobile breakpoint.
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // App.scss offsets `.main-content` with the GLOBAL `--sidebar-width`
  // variable. Keep it in sync with the real rendered width. On mobile the
  // sidebar is an off-canvas drawer, so it must not reserve any space.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return undefined;

    const syncWidth = () => {
      const width = isMobile ? 0 : el.getBoundingClientRect().width;
      document.documentElement.style.setProperty(
        "--sidebar-width",
        `${width}px`,
      );
    };

    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [collapsed, isMobile, user]);

  // Escape closes the mobile drawer.
  useEffect(() => {
    if (!mobileOpen || !isMobile) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && onMobileClose) onMobileClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, isMobile, onMobileClose]);

  const closeDrawer = useCallback(() => {
    if (onMobileClose) onMobileClose();
  }, [onMobileClose]);

  if (!user) {
    return null;
  }

  const userRoles = user.authorities || [];
  const iconOnly = collapsed && !isMobile; // the drawer is always full width

  const visibleSections = SIDEBAR_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      item.roles.some((role) => hasRole(role)),
    ),
  })).filter((section) => section.items.length > 0);

  const footerItems = SIDEBAR_FOOTER_ITEMS.filter((item) =>
    item.roles.some((role) => hasRole(role)),
  );

  const renderItem = (item) => (
    <SidebarItem
      key={item.name}
      item={item}
      isActive={isItemActive(item, pathname)}
      userRoles={userRoles}
      notificationCount={
        item.countKey ? notificationCounts[item.countKey] || 0 : 0
      }
      collapsed={iconOnly}
      onNavigate={closeDrawer}
    />
  );

  return (
    <>
      <div
        className={`sidebar-overlay ${mobileOpen && isMobile ? "is-visible" : ""}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <aside
        id="app-sidebar"
        ref={sidebarRef}
        className={[
          "sidebar",
          iconOnly ? "sidebar--collapsed" : "",
          mobileOpen ? "sidebar--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Primary"
      >
        <div className="sidebar-brand" title={user.companyName || "HRLabs"}>
          <img
            src={user.logo && user.logoType ? `data:${user.logoType};base64,${user.logo}` : DefaultLogo}
            alt={`${user.companyName || "HRLabs"} logo`}
          />
          <span className={iconOnly ? "sidebar-brand-name is-hidden" : "sidebar-brand-name"}>
            {user.companyName || "HRLabs"}
          </span>
        </div>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {visibleSections.map((section, index) => (
            <div className="sidebar-section" key={section.id}>
              {iconOnly ? (
                index > 0 && (
                  <div className="sidebar-divider" role="separator" />
                )
              ) : (
                <div className="sidebar-section-title">{section.title}</div>
              )}
              <ul className="sidebar-list">{section.items.map(renderItem)}</ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          {footerItems.length > 0 && (
            <ul className="sidebar-list">{footerItems.map(renderItem)}</ul>
          )}

          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={collapsed}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} strokeWidth={1.8} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} strokeWidth={1.8} aria-hidden="true" />
            )}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
